import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Temporary probe page to verify Realtime over domain. Remove after smoke test.
// Route: /probe-realtime

type LogItem = {
  ts: string;
  level: 'info' | 'error';
  message: string;
  payload?: unknown;
};

const now = () => new Date().toLocaleTimeString();

const useRealtimeProbe = () => {
  const [status, setStatus] = useState<'INIT' | 'SUBSCRIBING' | 'SUBSCRIBED' | 'CLOSED' | 'CHANNEL_ERROR'>('INIT');
  const [errors, setErrors] = useState<string[]>([]);
  const [logs, setLogs] = useState<LogItem[]>([]);
  const clientRef = useRef<SupabaseClient | null>(null);

  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

  const canInit = useMemo(() => Boolean(url && anon), [url, anon]);

  useEffect(() => {
    if (!canInit) {
      setErrors((e) => [...e, 'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY']);
      return;
    }

    setStatus('SUBSCRIBING');
    const supabase = createClient(url!, anon!, {
      realtime: {
        params: {
          vsn: '2.0.0',
        },
      },
    });
    clientRef.current = supabase;

    const log = (level: LogItem['level'], message: string, payload?: unknown) =>
      setLogs((prev) => [{ ts: now(), level, message, payload }, ...prev].slice(0, 200));

    const channel = supabase
      .channel('public:messages')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          log('info', 'New message (INSERT) received', payload);
        }
      )
      .subscribe((chStatus) => {
        log('info', `Channel status: ${chStatus}`);
        if (chStatus === 'SUBSCRIBED') setStatus('SUBSCRIBED');
        if (chStatus === 'CHANNEL_ERROR') setStatus('CHANNEL_ERROR');
        if (chStatus === 'CLOSED') setStatus('CLOSED');
      });

    return () => {
      supabase.removeChannel(channel);
      setStatus('CLOSED');
    };
  }, [canInit, url, anon]);

  return { status, errors, logs, url, anon };
};

const badgeClass = (status: string) => {
  switch (status) {
    case 'SUBSCRIBED':
      return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800';
    case 'SUBSCRIBING':
      return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800';
    case 'CHANNEL_ERROR':
      return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800';
    default:
      return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800';
  }
};

const RealtimeProbePage: React.FC = () => {
  const { status, errors, logs, url, anon } = useRealtimeProbe();

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-xl font-semibold mb-4">Realtime Smoke Test</h1>
      <div className="mb-4 space-y-2">
        <div>
          <span className={badgeClass(status)}>Status: {status}</span>
        </div>
        <div className="text-xs text-gray-700">
          <div><b>VITE_SUPABASE_URL:</b> {url || <span className="text-red-600">not set</span>}</div>
          <div className="truncate"><b>VITE_SUPABASE_ANON_KEY:</b> {anon ? anon.slice(0, 12) + '…' : <span className="text-red-600">not set</span>}</div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <div className="font-medium mb-1">Errors</div>
          <ul className="list-disc pl-5">
            {errors.map((e, i) => (<li key={i}>{e}</li>))}
          </ul>
        </div>
      )}

      <div className="mb-4">
        <div className="text-sm font-medium mb-2">How to trigger an event (run from your terminal):</div>
        <pre className="whitespace-pre-wrap break-words text-xs bg-gray-50 p-3 rounded border border-gray-200">{`curl -i -X POST 'https://api.arendapro.plus/rest/v1/messages' \
  -H 'apikey: <VITE_SUPABASE_ANON_KEY>' \
  -H 'Authorization: Bearer <VITE_SUPABASE_ANON_KEY>' \
  -H 'Content-Type: application/json' \
  -H 'Prefer: return=representation' \
  --data '{"content":"hello from probe"}'`}
        </pre>
      </div>

      <div className="rounded-md border border-gray-200">
        <div className="px-3 py-2 border-b border-gray-200 text-sm font-medium">Recent logs</div>
        <div className="max-h-80 overflow-auto text-xs">
          {logs.length === 0 ? (
            <div className="p-3 text-gray-500">No logs yet. Waiting for events…</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {logs.map((l, idx) => (
                <li key={idx} className="p-3">
                  <div className="flex items-center justify-between">
                    <span className={l.level === 'error' ? 'text-red-700' : 'text-gray-800'}>
                      {l.ts} — {l.message}
                    </span>
                    <span className={`text-[10px] uppercase ${l.level === 'error' ? 'text-red-600' : 'text-gray-500'}`}>{l.level}</span>
                  </div>
                  {l.payload !== undefined && (
                    <pre className="mt-2 bg-gray-50 p-2 rounded border border-gray-100 overflow-auto">{JSON.stringify(l.payload, null, 2)}</pre>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RealtimeProbePage;
